/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable no-restricted-imports */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  createSlice,
  injectReducers,
  PayloadAction,
  Draft,
  SliceCaseReducers,
  CaseReducer,
} from '@apia/store';
import { TPanelId, TPanelType } from '../types';

type ValidateSliceCaseReducers<S, ACR extends SliceCaseReducers<S>> = ACR & {
  [T in keyof ACR]: ACR[T] extends {
    reducer(s: S, action?: infer A): any;
  }
    ? {
        prepare(...a: never[]): Omit<A, 'type'>;
      }
    : {};
};

export type TPanelsSliceState = Record<string, Record<string, unknown>>;

export type TReducers<
  State = any,
  CR extends SliceCaseReducers<State> = SliceCaseReducers<State>,
> = ValidateSliceCaseReducers<State, CR>;

type TPanelsReducers<
  State = any,
  CR extends SliceCaseReducers<State> = SliceCaseReducers<State>,
> = Record<string, TReducers<State, CR>>;

/**
 * Los updaters son funciones que permiten modificar la forma en la que un
 * panel modifica su estado, se puede pensar como un análogo del reducer
 *
 * {
 *   panelTypeA: {
 *     updaterA: (state, data) => { ...update panel store },
 *     updaterB: (state, data) => { ...update panel store },
 *   }
 * }
 */
export const reducers: TPanelsReducers<Record<string, unknown>> = {};

export function initPanelTypeUpdater(panelType: TPanelType) {
  if (!reducers[panelType]) {
    reducers[panelType] = {
      defaultReducer: (
        panelsState,
        payload: PayloadAction<{ name: string }>,
      ) => {
        Object.assign(panelsState, payload);
      },
    };
  }
}

/**
 * Permite registrar nuevas formas de actualizar el estado de un tipo de panel
 */
export function registerReducers<
  State = any,
  CR extends SliceCaseReducers<State> = SliceCaseReducers<State>,
>(panelType: TPanelType, newReducers: TReducers<State, CR>) {
  initPanelTypeUpdater(panelType);
  Object.assign(reducers[panelType], newReducers);
}

export function initPanelState(
  state: Draft<TPanelsSliceState>,
  panelType: TPanelType,
  panelId: TPanelId,
  initialState: any,
) {
  if (!state[panelType]) {
    state[panelType] = {};
  }
  if (!state[panelType][panelId]) state[panelType][panelId] = {};
  state[panelType][panelId] = initialState;
}

export const panelsSlice = createSlice({
  name: 'panelsSlice',
  initialState: {} as TPanelsSliceState,
  reducers: {
    initState(
      state,
      {
        payload: { panelId, panelType, initialState },
      }: PayloadAction<{
        panelType: TPanelType;
        panelId: TPanelId;
        reducer?: string; // reducer will be used to customize the way the slice is updated
        initialState: any;
      }>,
    ) {
      if (!state[panelType]?.[panelId]) {
        initPanelState(state, panelType, panelId, initialState);
        state[panelType][panelId] = initialState;
      }
    },
    updatePanelStore(
      state,
      {
        payload: { data, panelId, panelType, reducer, initialState },
      }: PayloadAction<{
        panelType: TPanelType;
        panelId: TPanelId;
        reducer?: string; // Updater will be used to customize the way the slice is updated
        data: any;
        initialState?: any;
      }>,
    ) {
      if (!state[panelType]?.[panelId]) {
        initPanelState(state, panelType, panelId, initialState ?? {});
      }
      const panelsStore = state[panelType][panelId];
      initPanelTypeUpdater(panelType);

      if (!reducers[panelType][reducer ?? 'default']) {
        return;
      }

      const currentReducer = reducers[panelType][
        reducer ?? 'default'
      ] as CaseReducer;
      currentReducer(panelsStore, data);
    },
  },
});

export const { useSelector: usePanelStoreSelector, store: panelsStore } =
  injectReducers({
    panelsSlice: panelsSlice.reducer,
  });

export const panelsActions = panelsSlice.actions;
